/**
 * @description
 * Usage reports tab component for displaying Anthropic usage data and charts.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import type { UsageReportsProps } from "./types";

export function UsageReports({
  usageData,
  usageStartingAt,
  usageEndingAt,
  usageBucketWidth,
  workspaces,
  loading: _loading,
  onSetUsageStartingAt,
  onSetUsageEndingAt,
  onSetUsageBucketWidth,
  onFetchUsage,
}: UsageReportsProps) {
  const getWorkspaceName = (workspaceId: string | null): string => {
    if (!workspaceId) return "N/A";
    const workspace = workspaces.find((w) => w.anthropicId === workspaceId);
    return workspace ? workspace.name : workspaceId;
  };

  const calculateTotalTokens = () => {
    let uncached = 0;
    let cacheCreation1h = 0;
    let cacheCreation5m = 0;
    let cacheRead = 0;
    let output = 0;

    usageData.forEach((bucket) => {
      bucket.results.forEach((result) => {
        uncached += result.uncached_input_tokens;
        cacheCreation1h += result.cache_creation.ephemeral_1h_input_tokens;
        cacheCreation5m += result.cache_creation.ephemeral_5m_input_tokens;
        cacheRead += result.cache_read_input_tokens;
        output += result.output_tokens;
      });
    });

    return { uncached, cacheCreation1h, cacheCreation5m, cacheRead, output };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Usage Reports</h2>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div>
          <label className="text-sm font-medium">Start Time</label>
          <input
            type="datetime-local"
            value={usageStartingAt.slice(0, 16)}
            onChange={(e) =>
              onSetUsageStartingAt(new Date(e.target.value).toISOString())
            }
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="text-sm font-medium">End Time</label>
          <input
            type="datetime-local"
            value={usageEndingAt.slice(0, 16)}
            onChange={(e) =>
              onSetUsageEndingAt(new Date(e.target.value).toISOString())
            }
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Bucket Width</label>
          <select
            value={usageBucketWidth}
            onChange={(e) =>
              onSetUsageBucketWidth(e.target.value as "1d" | "1h" | "1m")
            }
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="1d">1 Day</option>
            <option value="1h">1 Hour</option>
            <option value="1m">1 Minute</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={onFetchUsage}
            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Fetch Usage
          </button>
        </div>
      </div>

      {usageData.length > 0 && (
        <>
          <div className="grid gap-4 md:grid-cols-5">
            <div className="rounded-md border p-4">
              <p className="text-sm font-medium text-muted-foreground">
                Uncached Input
              </p>
              <p className="mt-2 text-2xl font-bold">
                {calculateTotalTokens().uncached.toLocaleString()}
              </p>
            </div>
            <div className="rounded-md border p-4">
              <p className="text-sm font-medium text-muted-foreground">
                Cache 1h
              </p>
              <p className="mt-2 text-2xl font-bold">
                {calculateTotalTokens().cacheCreation1h.toLocaleString()}
              </p>
            </div>
            <div className="rounded-md border p-4">
              <p className="text-sm font-medium text-muted-foreground">
                Cache 5m
              </p>
              <p className="mt-2 text-2xl font-bold">
                {calculateTotalTokens().cacheCreation5m.toLocaleString()}
              </p>
            </div>
            <div className="rounded-md border p-4">
              <p className="text-sm font-medium text-muted-foreground">
                Cache Read
              </p>
              <p className="mt-2 text-2xl font-bold">
                {calculateTotalTokens().cacheRead.toLocaleString()}
              </p>
            </div>
            <div className="rounded-md border p-4">
              <p className="text-sm font-medium text-muted-foreground">
                Output
              </p>
              <p className="mt-2 text-2xl font-bold">
                {calculateTotalTokens().output.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {usageData.map((bucket, bucketIndex) => (
              <div key={bucketIndex} className="rounded-md border p-4">
                <h3 className="mb-3 font-medium">
                  {new Date(bucket.starting_at).toLocaleString()} -{" "}
                  {new Date(bucket.ending_at).toLocaleString()}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b">
                      <tr>
                        <th className="pb-2 text-left font-medium">
                          Workspace
                        </th>
                        <th className="pb-2 text-left font-medium">
                          Model
                        </th>
                        <th className="pb-2 text-right font-medium">
                          Uncached
                        </th>
                        <th className="pb-2 text-right font-medium">
                          Cache Read
                        </th>
                        <th className="pb-2 text-right font-medium">
                          Output
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {bucket.results.map((result, resultIndex) => (
                        <tr key={resultIndex}>
                          <td className="py-2">
                            {getWorkspaceName(result.workspace_id)}
                          </td>
                          <td className="py-2 text-xs">
                            {result.model || "N/A"}
                          </td>
                          <td className="py-2 text-right">
                            {result.uncached_input_tokens.toLocaleString()}
                          </td>
                          <td className="py-2 text-right">
                            {result.cache_read_input_tokens.toLocaleString()}
                          </td>
                          <td className="py-2 text-right">
                            {result.output_tokens.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
